import Button from '@carvana/ds-button';
import { OnEscapeListener } from '@carvana/merch-components';
import { useClientSide } from '@carvana/merch-react-hooks';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import FocusLock from 'react-focus-lock';
import { modalZIndex } from '~/constants/common/zIndex';
import Close from '~/icons/Close';
import styles from './VideoLightbox.module.css';

const Portal = dynamic(
  () => import(/* webpackChunkName: "Portal" */ '@/common/Portal'),
  { ssr: false }
);

type OwnProps = {
  isOpen: boolean;
  title: string;
  videoUrl: string;
  onClose: () => void;
};

const LIGHTBOX_ID = 'marketing-carousel-video-lightbox';

export default function VideoLightbox({
  isOpen,
  title,
  videoUrl,
  onClose
}: OwnProps) {
  const isClientSide = useClientSide();

  return isClientSide ? (
    <Portal id={LIGHTBOX_ID} open={isOpen}>
      <FocusLock autoFocus={false} group={LIGHTBOX_ID}>
        <OnEscapeListener onEscape={onClose}>
          <div
            aria-label={title}
            aria-modal="true"
            className="fixed inset-0 bg-black"
            role="dialog"
            style={{ zIndex: modalZIndex }}
            data-testid={LIGHTBOX_ID}
          >
            <Button
              aria-label="Close video"
              className="group absolute right-2xl top-2xl md:right-3xl md:top-3xl"
              data-analytics-id={null}
              data-testid={`${LIGHTBOX_ID}-close`}
              icon
              onClick={onClose}
              size="sm"
              variant="primaryInverted"
            >
              <Close className="size-24 text-icon-strong group-hover:text-[unset] group-active:text-[unset]" />
            </Button>

            <div className="mx-auto flex h-full max-w-[1648px] items-center justify-center min-[768px]:px-[104px] py-3xl">
              <div
                className={cx(styles.videoBox, 'overflow-hidden sm:rounded-xl')}
              >
                <iframe
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                  data-testid={`${LIGHTBOX_ID}-iframe`}
                  src={videoUrl}
                  title={title}
                />
              </div>
            </div>
          </div>
        </OnEscapeListener>
      </FocusLock>
    </Portal>
  ) : null;
}
